'use client'

import { useState } from 'react'

import clsx from 'clsx'

import type { JobDescription, Location, MicroRole } from '@types'

import useHttpRequest from '@hooks/useHttpRequest'

import Button from './button'
import Combobox from './combobox'

import type { ButtonHTMLAttributes, FormEvent, FormHTMLAttributes, JSX } from 'react'

type SearchBarProps = {
  onSubmit: (search: { job: JobDescription; location: Location }) => void
  initialJob?: JobDescription
  initialLocation?: Location
  submitProps?: ButtonHTMLAttributes<HTMLButtonElement> & { [key: `data-${string}`]: string | undefined }
} & Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>

type Errors = { job?: string; location?: string }

/** Roles that start with the query first; otherwise the API's order. */
export function sortPrefixFirst(roles: MicroRole[], query: string): MicroRole[] {
  const prefix = query.toLowerCase()
  const startsWith = (role: MicroRole): boolean => role.microRoles.toLowerCase().startsWith(prefix)

  return [...roles.filter(startsWith), ...roles.filter((role) => !startsWith(role))]
}

/** The API sends the state in lower case ("Austin, tx"); US addresses write it in capitals. */
const locationLabel = (location: Location): string => {
  const { displayName, state } = location
  if (!state || !displayName.toLowerCase().endsWith(state.toLowerCase())) return displayName

  return displayName.slice(0, -state.length) + state.toUpperCase()
}

const normaliseLocation = (location: Location): Location => ({
  ...location,
  displayName: locationLabel(location),
  state: location.state.toUpperCase(),
})

const ICON = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  className: 'size-5',
} as const

// lucide: search (lucide-react 0.564.0), inlined so the marketing site takes no icon dependency.
const searchIcon = (
  <svg {...ICON}>
    <path d="m21 21-4.34-4.34" />
    <circle cx="11" cy="11" r="8" />
  </svg>
)

// lucide: map-pin (lucide-react 0.564.0)
const mapPinIcon = (
  <svg {...ICON}>
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

/** Job title and location, both autocompleted. Free text is a valid job; the location must be picked. */
export default function SearchBar({
  onSubmit,
  initialJob,
  initialLocation,
  submitProps,
  className,
  ...rest
}: SearchBarProps): JSX.Element {
  const { get } = useHttpRequest()

  const [job, setJob] = useState<JobDescription>(initialJob ?? { micro: '', macro: '' })
  const [location, setLocation] = useState<Location | undefined>(initialLocation && normaliseLocation(initialLocation))
  const [locationText, setLocationText] = useState(location?.displayName ?? '')
  const [errors, setErrors] = useState<Errors>({})

  const fetchJobs = async (query: string): Promise<MicroRole[]> =>
    sortPrefixFirst(
      (await get<MicroRole[]>(`jobs/autocomplete?query=${encodeURIComponent(query)}`, { service: 'web' })) ?? [],
      query,
    )

  const fetchLocations = async (query: string): Promise<Location[]> =>
    (await get<Location[]>(`locations/autocomplete?query=${encodeURIComponent(query)}`, { service: 'web' })) ?? []

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    const next: Errors = {
      job: job.micro.trim() ? undefined : 'Enter a job title',
      location: location ? undefined : locationText.trim() ? 'Select a location from the list' : 'Enter a location',
    }
    setErrors(next)

    if (location && !next.job) onSubmit({ job, location })
  }

  return (
    <form
      role="search"
      {...rest}
      onSubmit={handleSubmit}
      className={clsx('flex flex-col gap-4 sm:flex-row sm:items-end', className)}
    >
      <div className="sm:flex-1">
        <Combobox
          label="Job title"
          icon={searchIcon}
          placeholder="Job title"
          minChars={1}
          fetchOptions={fetchJobs}
          getOptionLabel={(role) => role.microRoles}
          inputValue={job.micro}
          onInputChange={(text) => {
            setJob({ micro: text, macro: '' })
            setErrors((current) => ({ ...current, job: undefined }))
          }}
          onSelect={(role) => setJob({ micro: role.microRoles, macro: role.macroRoles ?? '' })}
          error={errors.job}
        />
      </div>
      <div className="sm:flex-1">
        <Combobox
          label="Location"
          icon={mapPinIcon}
          placeholder="City or ZIP code"
          minChars={3}
          fetchOptions={fetchLocations}
          getOptionLabel={locationLabel}
          inputValue={locationText}
          onInputChange={(text) => {
            setLocationText(text)
            setLocation(undefined)
            setErrors((current) => ({ ...current, location: undefined }))
          }}
          onSelect={(picked) => {
            const normalised = normaliseLocation(picked)
            setLocationText(normalised.displayName)
            setLocation(normalised)
            setErrors((current) => ({ ...current, location: undefined }))
          }}
          error={errors.location}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Button {...submitProps} type="submit" color="primary" variant="solid" size="lg">
          Search
        </Button>
        {/* Matches the error line each field reserves (Combobox), so the button stays level with the fields. */}
        <span aria-hidden="true" className="hidden min-h-[1lh] text-base sm:block" />
      </div>
    </form>
  )
}
